import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, Loader2, PenLine, Users, X } from "lucide-react";
import { ConsentToggle } from "@/components/t4r/learners/ConsentPill";
import { EmailComposer } from "@/components/t4r/email/EmailComposer";

const GroupList = ({ groups, onOpen }) => (
  <div className="space-y-2" data-testid="email-group-list">
    {groups === null && <div className="flex justify-center py-6"><Loader2 className="animate-spin text-slate-400" size={18} /></div>}
    {groups?.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No groups yet. Import an EOI sheet to create one.</p>}
    {(groups || []).map((g) => (
      <button key={g.id} onClick={() => onOpen(g.id)} className="flex w-full items-center justify-between rounded-lg border border-border px-3 py-2.5 text-left transition-colors hover:border-green-300 hover:bg-green-50/50" data-testid={`email-group-${g.id}`}>
        <span className="flex items-center gap-2 text-sm font-medium"><Users size={14} className="text-green-700" /> {g.name}</span>
        <span className="text-xs text-muted-foreground">{g.sendable} of {g.members} can be emailed</span>
      </button>
    ))}
  </div>
);

const Compose = ({ group, onWrite }) => {
  const count = group.members.filter((m) => m.sendable).length;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3" data-testid="group-compose">
      <p className="text-xs text-muted-foreground">{count} {count === 1 ? "person has" : "people have"} consent and emails on. Only they are emailed.</p>
      <Button size="sm" className="gap-1 bg-green-700 text-white hover:bg-green-800" disabled={!count} onClick={onWrite} data-testid="group-write-email"><PenLine size={12} /> Write email</Button>
    </div>
  );
};

const GroupDetail = ({ group, onBack, reload, onLearnersChanged, onWrite }) => {
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from this group? Their learner record is kept.`)) return;
    try { await api.delete(`/lms/admin/email-groups/${group.id}/members/${m.id}`); reload(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="email-group-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-xs font-medium text-green-800 hover:underline" data-testid="email-group-back"><ChevronLeft size={12} /> All groups</button>
      <Compose group={group} onWrite={onWrite} />
      <div className="max-h-[260px] overflow-y-auto rounded-md border border-border">
        {group.members.map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-2 border-b border-border px-3 py-2 text-xs last:border-0" data-testid={`group-member-${m.id}`}>
            <div className="min-w-0"><p className="truncate font-medium">{m.name}</p><p className="truncate text-muted-foreground">{m.email}</p></div>
            <div className="flex shrink-0 items-center gap-2">
              <ConsentToggle learner={m} onChanged={() => { reload(); onLearnersChanged(); }} />
              <button onClick={() => remove(m)} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600" title="Remove from group" data-testid={`group-member-remove-${m.id}`}><X size={12} /></button>
            </div>
          </div>
        ))}
        {group.members.length === 0 && <p className="p-4 text-center text-xs text-muted-foreground">No members.</p>}
      </div>
      {group.sends.length > 0 && (
        <div className="text-xs" data-testid="group-send-history">
          <p className="mb-1 font-semibold text-slate-700">Sent emails</p>
          {group.sends.map((s) => (
            <div key={s.id} className="text-muted-foreground" data-testid={`group-send-${s.id}`}>
              <p>{new Date(s.created_at).toLocaleString()}: "{s.subject}" {s.status === "sending" ? `sending to ${s.recipients}…` : <><span className={s.sent ? "text-green-700" : "text-slate-500"}>sent to {s.sent}</span>{s.failed ? <span className="text-red-700">, {s.failed} failed</span> : null}</>} by {s.by}</p>
              {s.errors?.length > 0 && <p className="pl-3 text-[11px] text-red-700">{s.errors.join(" · ")}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const EmailGroupsDialog = ({ open, onClose, onLearnersChanged }) => {
  const [groups, setGroups] = useState(null);
  const [group, setGroup] = useState(null);
  const loadList = useCallback(() => api.get("/lms/admin/email-groups").then(({ data }) => setGroups(data.groups)).catch(() => toast.error("Couldn't load groups")), []);
  const openGroup = useCallback((id) => api.get(`/lms/admin/email-groups/${id}`).then(({ data }) => setGroup(data)).catch(() => toast.error("Couldn't load group")), []);
  useEffect(() => { if (open) { setGroups(null); loadList(); setGroup(null); } }, [open, loadList]);
  const sending = group?.sends?.some((s) => s.status === "sending");
  const [writing, setWriting] = useState(false);
  useEffect(() => { if (!open) setWriting(false); }, [open]);
  useEffect(() => {
    if (!sending) return undefined;
    const t = setTimeout(() => openGroup(group.id), 2500);
    return () => clearTimeout(t);
  }, [sending, group, openGroup]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${writing ? "max-w-[1280px]" : "max-w-2xl"} max-h-[92vh] overflow-y-auto grid-cols-[minmax(0,1fr)]`} data-testid="email-groups-dialog">
        <DialogHeader>
          <DialogTitle>{group ? (writing ? `Email: ${group.name}` : group.name) : "Email groups"}</DialogTitle>
          <DialogDescription className="text-xs">{group ? `${group.members.length} ${group.members.length === 1 ? "person" : "people"}, ${group.members.filter((m) => m.sendable).length} with consent and emails on` : "Write one email and send it to everyone in a group who has given consent."}</DialogDescription>
        </DialogHeader>
        {group && writing && (
          <div className="space-y-2">
            <button onClick={() => setWriting(false)} className="inline-flex items-center gap-1 text-xs font-medium text-green-800 hover:underline" data-testid="email-compose-back"><ChevronLeft size={12} /> Back to group</button>
            <EmailComposer mode="send" groupId={group.id} count={group.members.filter((m) => m.sendable).length} onSent={() => { setWriting(false); openGroup(group.id); }} />
          </div>
        )}
        {group && !writing && <GroupDetail group={group} onBack={() => { setGroup(null); loadList(); }} reload={() => openGroup(group.id)} onLearnersChanged={onLearnersChanged} onWrite={() => setWriting(true)} />}
        {!group && <GroupList groups={groups} onOpen={openGroup} />}
      </DialogContent>
    </Dialog>
  );
};
